import { Injectable, inject } from '@angular/core';
import { ExpenditureOptions } from '../../../../../core/constants-repository/project-calculation.constants';
import { ExpenditureEntryService } from '../../project-edit/manage-project/project-calculation-form/services/expenditure-entry.service';
import { ProjectExpenditureEntryModel } from '../../../../../core/models/project/project-expenditure-entry.model';
import { ITimeBlockComponentItem } from '../../../../../features/calendar/components/time-block/types/time-block-component-item-types';
import { TimeBlockDurationCalculationService } from '../../../../../features/calendar/components/time-block/calculation/duration/time-block-duration-calculation.service';
import { assert } from '../../../../../core/assert/assert';
import { ProjectModel } from '../../../../../core/models/project/project.model';
import { TimeBlockProjectModel } from '../../../../../core/models/timeblock/time-block.model';
import { DateTimeHelper } from '../../../../../features/calendar/util/date-time-helper';
import { TimeBlockCost, TimeBlockFee } from '../../project-edit/edit-project.types';
import { MathUtil } from '../../../../../features/calendar/util/math';
import { TimeBlockCostCalculationService } from './time-block-cost-calculation.service';
import { TimeBlockWithTask } from '../../../../../shared/data-types/shared-time-block-types';

@Injectable({
  providedIn: 'root',
})
export class ProjectMonetaryCalculationService {
  // DI
  private readonly expenditureEntryService = inject(ExpenditureEntryService);
  private readonly timeBlockDurationCalculationService = inject(
    TimeBlockDurationCalculationService,
  );
  private readonly timeBlockCostCalculationService = inject(TimeBlockCostCalculationService);

  public calculateFeeSum(timeBlockFees: TimeBlockFee[]): number {
    return timeBlockFees.reduce((accum, current) => {
      const timeInSeconds = this.timeBlockDurationCalculationService.getTimeBlockTimeInSeconds(
        current.timeBlockWithTask.timeBlock,
      );
      accum += DateTimeHelper.secondsToHours(timeInSeconds) * current.hourlyRate;
      return MathUtil.roundTwoDecimals(accum);
    }, 0);
  }

  public calcPersonnelCosts(timeBlocksWithTask: TimeBlockWithTask[]): number {
    const timeBlockCosts = this.timeBlockCostCalculationService.timeBlockCosts(
      timeBlocksWithTask,
      true,
      {
        start: null,
        end: null,
      },
    );
    return timeBlockCosts.reduce((acc, cur) => {
      return this.calculateCostSum([cur]) + acc;
    }, 0);
  }

  public getExpenditureEntriesForTimeBlock(
    timeBlock: ITimeBlockComponentItem,
    project: ProjectModel,
  ): readonly ProjectExpenditureEntryModel[] {
    if (project.expenditureType === ExpenditureOptions.HourlyRatePerTask) {
      return this.expenditureEntryService.getEntriesForTask(
        project,
        +(timeBlock.timeBlockModel as TimeBlockProjectModel).task.id,
      );
    } else if (project.expenditureType === ExpenditureOptions.HourlyRatePerMember) {
      return this.expenditureEntryService.getEntriesForMember(
        project,
        +(timeBlock.timeBlockModel as TimeBlockProjectModel).userId,
      );
    } else if (project.expenditureType === ExpenditureOptions.HourlyRateFixed) {
      return this.expenditureEntryService.getEntriesFixed(project);
    } else if (project.expenditureType === ExpenditureOptions.ExpendituresInHours) {
      return [];
    }
    assert(false, 'Wrong expenditure type.');
  }

  private calculateCostSum(timeBlockCosts: TimeBlockCost[]): number {
    return timeBlockCosts.reduce((accum, current) => {
      const timeInSeconds = this.timeBlockDurationCalculationService.getTimeBlockTimeInSeconds(
        current.timeBlockWithTask.timeBlock,
      );
      accum += DateTimeHelper.secondsToHours(timeInSeconds) * current.costRate;
      return MathUtil.roundTwoDecimals(accum);
    }, 0);
  }
}
